import {
  ApiError,
  queryKeys,
  type UpdateVendingMachineInput,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Images, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { ImageLibraryDialog } from './image-library-grid.js'

const MAX_LOGO_BYTES = 5 * 1024 * 1024
const MAX_MESSAGE_LENGTH = 60

/** What the tablet says when no message is set — keep in step with the
 *  Android attract screens. */
const DEFAULT_MESSAGE = { cabinet: 'Shop this cabinet', vending: 'Touch to begin' } as const

/** The logo and headline on a machine tablet's start screen (t1293). The logo
 *  is a picture from the same library products use; the machine just keeps its
 *  URL. */
export function StartScreenCard({ machine }: { machine: VendingMachine }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [message, setMessage] = useState(machine.startScreenMessage ?? '')

  const canManage = can('kiosks:manage')
  const canUpload = canManage && can('catalog:write')

  const save = useMutation({
    mutationFn: (patch: UpdateVendingMachineInput) =>
      api.patch<VendingMachine>(`/vending/machines/${machine.id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machine(machine.id) })
      toast.success('Saved')
    },
    onError: (err) =>
      toast.error('Could not save', err instanceof ApiError ? err.message : undefined),
  })

  async function upload(file: File) {
    if (!file.type.startsWith('image/') && !/\.jxl$/i.test(file.name)) {
      toast.error('Could not upload', 'Only image files are supported.')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Could not upload', 'Images must be 5 MB or smaller.')
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { url } = await api.postFile<{ url: string }>('/products/images', formData)
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
      save.mutate({ startScreenLogoUrl: url })
    } catch (err) {
      toast.error('Could not upload the image', err instanceof ApiError ? err.message : undefined)
    } finally {
      setUploading(false)
    }
  }

  const trimmed = message.trim()
  const messageChanged = trimmed !== (machine.startScreenMessage ?? '')

  return (
    <Card className="sm:col-span-2">
      <CardHeader>
        <CardTitle>Start screen</CardTitle>
        <CardDescription>
          What a customer sees before they touch the tablet. The logo fills the whole screen, scaled
          to fit without stretching or cropping. Changes reach the tablet within a minute or so.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm font-medium">Logo</p>
          <div className="flex flex-wrap items-center gap-3">
            {machine.startScreenLogoUrl ? (
              <img
                src={machine.startScreenLogoUrl}
                alt="Start screen logo"
                className="max-h-24 max-w-48 rounded-md border border-border bg-background object-contain p-1"
              />
            ) : (
              <p className="text-sm text-foreground-muted">No logo — the tablet shows none.</p>
            )}
            {canManage ? (
              <div className="flex flex-wrap gap-2">
                {canUpload ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload /> Upload logo
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setLibraryOpen(true)}
                >
                  <Images /> Choose from library
                </Button>
                {machine.startScreenLogoUrl ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ startScreenLogoUrl: null })}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.jxl"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void upload(file)
              e.target.value = ''
            }}
          />
          <ImageLibraryDialog
            open={libraryOpen}
            onOpenChange={setLibraryOpen}
            onSelect={(url) => save.mutate({ startScreenLogoUrl: url })}
          />
        </div>

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (canManage && messageChanged) {
              save.mutate({ startScreenMessage: trimmed === '' ? null : trimmed })
            }
          }}
        >
          <Field
            label="Message"
            hint="Shown in large type on the start screen. Leave blank for the default."
          >
            {(props) => (
              <Input
                {...props}
                value={message}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder={DEFAULT_MESSAGE[machine.kind]}
                disabled={!canManage}
                onChange={(e) => setMessage(e.target.value)}
                className="w-72"
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="secondary"
            disabled={!canManage || !messageChanged}
            loading={save.isPending}
          >
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
